import { TFunction } from 'i18next'
import { z } from 'zod'

import { Person } from '@/components/business/person-column'
import { BadgeColorKeys } from '@/components/ui/badge'
import { StatusTabOption } from '@/components/ui/status-tabs'
import {
  MaintenanceLocation,
  MaintenancePriority,
  MaintenanceRequest,
  MaintenanceSource,
  MaintenanceStats,
  MaintenanceStatus,
  MaintenanceStatusEnum,
} from '@/models/features'
import { UserRoleEnum } from '@/models/features/user.models'
import { Building, PmsIntegration } from '@/reducers/types'

// Note: checks the raw PMS ids directly rather than the `*Config` booleans computed in
// `formatBuildingsForFilter` (buildingSelectorReducer.ts) - those are only present on buildings
// sourced from the building-filter list/`activeBuilding`, not on a plain `Building` passed in via
// the Resident Profile embed's `client.building`. Mirrors the same check in
// RequestDetails.jsx's integration-provider detection.
export const isPmsIntegratedBuilding = (building?: Building | null): boolean =>
  !!(
    building &&
    ((building.yardiManagementId && building.yardiPropertyId) ||
      (building.entrataManagementId && building.entrataPropertyId) ||
      (building.realPageManagementId && building.realPagePropertyId) ||
      building.resmanPropertyId ||
      building.rentManagerPropertyId)
  )

const MAINTENANCE_STATUS_COLOR_MAP: Record<MaintenanceStatus, BadgeColorKeys> = {
  Canceled: 'red',
  Finished: 'green',
  'In Progress': 'blue',
  'On Hold': 'orange',
  Pending: 'neutral',
}

export const getMaintenanceStatusColor = (status: MaintenanceStatus): BadgeColorKeys =>
  MAINTENANCE_STATUS_COLOR_MAP[status] ?? 'neutral'

// Note: `allCount` is the BE's per-status total (EL-15063). A BE that doesn't send it yet still
// sends the legacy buckets, which together cover every request - use their sum instead.
export const getMaintenanceAllCount = (stats?: Partial<MaintenanceStats>): number =>
  stats?.allCount ??
  (stats?.pendingCount ?? 0) + (stats?.scheduledCount ?? 0) + (stats?.completedCount ?? 0)

// Note: `undefined` (no count badge on the tab) rather than 0 when the BE doesn't send per-status
// counts - a 0 would claim the status has no requests.
export const getMaintenanceStatusCounts = (
  stats?: Partial<MaintenanceStats>,
): Record<MaintenanceStatus, number | undefined> => {
  const hasStatusCounts = stats?.allCount !== undefined

  return {
    Canceled: hasStatusCounts ? (stats?.canceledCount ?? 0) : undefined,
    Finished: hasStatusCounts ? (stats?.finishedCount ?? 0) : undefined,
    'In Progress': hasStatusCounts ? (stats?.inProgressCount ?? 0) : undefined,
    'On Hold': hasStatusCounts ? (stats?.onHoldCount ?? 0) : undefined,
    Pending: hasStatusCounts ? (stats?.pendingCount ?? 0) : undefined,
  }
}

export const getMaintenanceStatusLabel = (
  status: MaintenanceStatus,
  t: TFunction<['maintenance']>,
): string => {
  const labels: Record<MaintenanceStatus, string> = {
    Canceled: t('maintenance:requests.filters.statusOptions.canceled'),
    Finished: t('maintenance:requests.filters.statusOptions.finished'),
    'In Progress': t('maintenance:requests.filters.statusOptions.inProgress'),
    'On Hold': t('maintenance:requests.filters.statusOptions.onHold'),
    Pending: t('maintenance:requests.filters.statusOptions.pending'),
  }
  return labels[status]
}

const MAINTENANCE_PRIORITY_COLOR_MAP: Record<MaintenancePriority, BadgeColorKeys> = {
  Critical: 'red',
  Emergency: 'red',
  High: 'orange',
  Low: 'blue',
  Med: 'orange',
  Routine: 'neutral',
}

export const getMaintenancePriorityColor = (priority: MaintenancePriority): BadgeColorKeys =>
  MAINTENANCE_PRIORITY_COLOR_MAP[priority] ?? 'neutral'

export const getMaintenancePriorityLabel = (
  priority: MaintenancePriority,
  t: TFunction<['maintenance']>,
): string => {
  const labels: Record<MaintenancePriority, string> = {
    Critical: t('maintenance:requests.filters.priorityOptions.critical'),
    Emergency: t('maintenance:requests.filters.priorityOptions.emergency'),
    High: t('maintenance:requests.filters.priorityOptions.high'),
    Low: t('maintenance:requests.filters.priorityOptions.low'),
    Med: t('maintenance:requests.filters.priorityOptions.medium'),
    Routine: t('maintenance:requests.filters.priorityOptions.routine'),
  }
  // Note: `priority` is typed as the known enum, but PMS integrations (Yardi, Entrata, ...) don't
  // actually validate against it - a raw unrecognized value (e.g. Yardi's "24 hours") must still
  // display as-is rather than render an empty badge, matching the old design's `Priority.jsx`.
  return labels[priority] ?? priority
}

// Note: the PMS-specific default column views (EL-15257). AppFolio has no mapping of its own, so
// it shares the Non-PMS view - same as a missing/unknown `pmsIntegration`.
export type MaintenancePmsType =
  | 'yardi'
  | 'realpage'
  | 'resman'
  | 'entrata'
  | 'rentmanager'
  | 'nonPms'

const PMS_INTEGRATION_TYPE_MAP: Partial<Record<PmsIntegration, MaintenancePmsType>> = {
  Entrata: 'entrata',
  RealPage: 'realpage',
  RentManager: 'rentmanager',
  ResMan: 'resman',
  Yardi: 'yardi',
}

// Note: reads the BE-computed `pmsIntegration` rather than the raw PMS ids (unlike
// isPmsIntegratedBuilding above) - the BE is the single source of truth for which PMS a
// building uses, e.g. ResMan also needs `resmanAccountId` there.
export const getMaintenancePmsType = (building?: Building | null): MaintenancePmsType =>
  PMS_INTEGRATION_TYPE_MAP[building?.pmsIntegration as PmsIntegration] ?? 'nonPms'

// Note: column ids hidden by default per PMS - every other column starts visible. The column
// order itself never depends on the PMS, see getMaintenanceColumns.
export const MAINTENANCE_HIDDEN_COLUMNS: Record<MaintenancePmsType, string[]> = {
  entrata: ['requestType', 'priority', 'address', 'description'],
  nonPms: ['priority', 'address'],
  realpage: ['requestType', 'priority', 'address', 'description'],
  rentmanager: ['requestType', 'address'],
  resman: ['requestType', 'address', 'description'],
  yardi: ['requestType', 'address'],
}

export const MAINTENANCE_DESCRIPTION_MAX_LENGTH = 100

// Note: cuts at the last full word that fits, so the "…" never lands mid-word. A single word
// longer than the limit has no earlier break, so it's hard-cut instead.
export const truncateAtWord = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  const slice = text.slice(0, maxLength + 1)
  const lastSpace = slice.lastIndexOf(' ')
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : text.slice(0, maxLength)
  return `${cut.trimEnd()}…`
}

// Note: `shortDescription` wins when present; PMS work orders often leave it empty and only
// carry the full `description`.
export const getMaintenanceDescription = ({
  shortDescription,
  description,
}: {
  shortDescription?: string | null
  description?: string | null
}): string => shortDescription?.trim() || description?.trim() || ''

export const MaintenanceStatusFilterEnum = z.enum(['all', ...MaintenanceStatusEnum.options])
export type MaintenanceStatusFilter = z.infer<typeof MaintenanceStatusFilterEnum>

export const getMaintenanceStatusTabs = (
  t: TFunction<['maintenance']>,
  counts: Record<MaintenanceStatus, number | undefined>,
  allCount: number,
): StatusTabOption<MaintenanceStatusFilter>[] => [
  {
    count: allCount,
    label: t('maintenance:requests.tabs.all'),
    value: 'all',
  },
  ...MaintenanceStatusEnum.options.map((status) => ({
    color: getMaintenanceStatusColor(status),
    count: counts[status],
    label: getMaintenanceStatusLabel(status, t),
    value: status,
  })),
]

// Note: ResMan's list of areas is joined into one value, same as the BE's CSV export does.
const formatMaintenanceLocation = (location?: MaintenanceLocation | null): string => {
  if (!location) return ''
  if (typeof location === 'string') return location.trim()
  return location
    .map((area) => (typeof area === 'string' ? area : area?.name)?.trim())
    .filter(Boolean)
    .join(', ')
}

// Note: the PMS-specific part of the "Request Object" column (EL-15239). The BE sends no combined
// field, so it's read from the row's own `source` - not the building's PMS, a building can still
// hold work orders synced before it switched. Natively created requests and Rent Manager have no
// secondary value by design, and neither does AppFolio - its sync stores no location field.
export const getMaintenanceSecondaryValue = ({
  source,
  subcategory,
  location,
}: Pick<MaintenanceRequest, 'source' | 'subcategory' | 'location'>): string => {
  switch (source) {
    case 'Yardi':
      return subcategory?.trim() ?? ''
    case 'RealPage':
    case 'ResMan':
    case 'Entrata':
      return formatMaintenanceLocation(location)
    default:
      return ''
  }
}

const PMS_SOURCES: MaintenanceSource[] = [
  'Yardi',
  'RealPage',
  'ResMan',
  'Entrata',
  'RentManager',
  'AppFolio',
]

// Note: PMS work orders carry their own `unit`. Natively created ones leave it empty and live in
// the requesting resident's apartment - the BE's unit filter matches either field the same way.
// A PMS common-area work order can still have a resident attached, so it never falls back.
export const getMaintenanceUnit = ({
  unit,
  source,
  clientId: client,
}: Partial<Pick<MaintenanceRequest, 'unit' | 'source' | 'clientId'>>): string => {
  const ownUnit = unit?.trim()
  if (ownUnit || (source && PMS_SOURCES.includes(source))) return ownUnit ?? ''
  return client?.apartment?.trim() ?? ''
}

// Note: "Unit 204 – Plumbing" / "Common Area – Pool". There's no common-area flag on the BE, so a
// work order without a unit is a common-area one - same rule as the Request type column.
export const getMaintenanceRequestObject = (
  request: Partial<
    Pick<MaintenanceRequest, 'unit' | 'source' | 'subcategory' | 'location' | 'clientId'>
  >,
  t: TFunction<['maintenance']>,
): string => {
  const unit = getMaintenanceUnit(request)
  const primary = unit
    ? t('maintenance:requests.cells.requestObjectUnit', { unit })
    : t('maintenance:requests.cells.commonArea')
  const secondary = getMaintenanceSecondaryValue(request)
  return secondary ? `${primary} – ${secondary}` : primary
}

// Note: the "Created By" column (EL-15428). Only our own create endpoint stores a creator (and
// only since 2026-10-21) - PMS syncs never do. A creator is a resident (linked to their profile)
// only when their role says so: the BE attaches a client record to any creator whose email has
// one, staff included and from any building. A deleted creator shows "-".
export const getMaintenanceCreatorPerson = ({
  createdBy,
}: Partial<Pick<MaintenanceRequest, 'createdBy'>>): Person | null => {
  if (!createdBy || typeof createdBy !== 'object' || createdBy.deleted) return null
  const isResident = createdBy.role === UserRoleEnum.enum.Client
  return {
    apartment: isResident ? createdBy.apartment : null,
    avatar: createdBy.avatar,
    buildingNumber: isResident ? createdBy.buildingNumber : null,
    clientId: isResident ? createdBy.clientId : null,
    email: createdBy.email,
    firstName: createdBy.firstName,
    kind: isResident ? 'resident' : 'staff',
    lastName: createdBy.lastName,
    phone: createdBy.phone,
    role: createdBy.role,
    street: isResident ? createdBy.street : null,
  }
}

// Note: a PMS work order without an ElevateOS creator was raised in the PMS by someone we can't
// map - EL-15428's "PMS Integration" badge. A native one without a creator just shows "-".
export const isMaintenanceCreatedByPms = ({
  createdBy,
  source,
}: Partial<Pick<MaintenanceRequest, 'createdBy' | 'source'>>): boolean =>
  !createdBy && !!source && PMS_SOURCES.includes(source)

export const getMaintenanceAssigneePerson = ({
  assignee,
}: Partial<Pick<MaintenanceRequest, 'assignee'>>): Person | null =>
  assignee
    ? {
        avatar: assignee.user.avatar,
        email: assignee.user.email,
        firstName: assignee.user.firstName,
        kind: 'staff',
        lastName: assignee.user.lastName,
        phone: assignee.phone,
        role: assignee.user.role,
      }
    : null
